export enum ReceiveType {
  AWS_PO = 'AWS_PO',
  ASN_AWS_PO = 'ASN_AWS_PO',
  ASN_OMD = 'ASN_OMD',
  ASN_RMA = 'ASN_RMA',
  ASN_WW_FA = 'ASN_WW_FA',
  ASN_JANUS = 'ASN_JANUS',
  ASN_AWS_OWNED_INVENTORY_RO = 'ASN_AWS_OWNED_INVENTORY_RO',
  INVENTORY_ADJUSTMENT = 'inventory_adjustment',
}

export enum ReceiveTypeNameMap {
  AWS_PO = 'PO',
  ASN_AWS_PO = 'ASN PO',
  ASN_OMD = 'OMD',
  ASN_RMA = 'RMA',
  ASN_WW_FA = 'FA',
  ASN_JANUS = 'JANUS',
  ASN_AWS_OWNED_INVENTORY_RO = 'AWS-OWNED INVENTORY',
}

export enum MaterialCategory {
  PART = 'PART',
  ASSEMBLY = 'ASSEMBLY',
}

export enum ReceiveReferenceIdType {
  NON_SPECIFIC = 'NON_SPECIFIC',
  ORDER_NUMBER = 'ORDER_NUMBER',
  SHIPMENT_ID = 'SHIPMENT_ID',
  TRACKING_NUMBER = 'TRACKING_NUMBER',
  INVENTORY_ADJUSTMENT = 'INVENTORY_ADJUSTMENT',
}

type ReferenceQueryRequestList = {
  type: ReceiveReferenceIdType;
  value: string;
};

export interface SearchReceiveReferencesRequest {
  referenceIdList: [ReferenceQueryRequestList];
  locationIdList?: string[];
  paginationSize?: number;
  paginationToken?: String;
}

// CreateReceive is used to create Receive request
export type CreateReceive = {
  referenceId: string;
  type: ReceiveType;
  models: CreateModel[];
};

export interface CreateModel extends Model {
  containers: Serial[];
}

export interface SearchReceiveReferencesResponse { }

export interface ValidationStatus {
  validationStatusType?: ValidationStatusType;
  statusCode?: string;
  statusMessage?: string;
}

export interface ReceiveStatus extends ValidationStatus {
  receiveStatusType?: AsyncState;
}

export enum ValidationStatusType {
  ERROR = 'ERROR',
  WARNING = 'WARNING',
  OK = 'OK',
}

export enum MetricSeverity {
  ERROR = 'ERROR',
  WARNING = 'WARNING',
  INFO = 'INFO',
}

export interface MetricPair {
  severity: MetricSeverity;
  metricType: MetricType;
}

export enum ExceptionTypes {
  'INVALID_SHIPMENT_REFERENCE',
  'INVALID_PO',
  'INVALID_PO_STATE',
  'INVALID_MODEL_SHIPMENT',
  'INVALID_MODEL_ORDER_NUMBER',
  'INVALID_SERIAL_SHIPMENT',
  'INVALID_SERIAL_ORDER_NUMBER',
  'DUPLICATE_SERIAL_SHIPMENT',
  'DUPLICATE_SERIAL_ORDER_NUMBER',
  'SERIAL_UNIQUENESS_FAILED_SHIPMENT',
  'SERIAL_UNIQUENESS_FAILED_ORDER_NUMBER',
  'SHIPMENT_REFERENCE_NOT_AVAILABLE',
  'SHIPMENT_REFERENCE_NOT_SCANNABLE',
  'SHIPMENT_REFERENCE_DAMAGED_OR_TORN',
}

export enum MetricType {
  IDENTIFIER_INVALID = 'IDENTIFIER_INVALID',
  ASN_INACTIVE = 'ASN_INACTIVE',
  PO_INACTIVE = 'PO_INACTIVE',
  UNDERLYING_PO_INACTIVE = 'UNDERLYING_PO_INACTIVE',
  MPN_INVALID = 'MPN_INVALID',
  MPN_MISMATCH_ASN = 'MPN_MISMATCH_ASN',
  MPN_MISMATCH_PO = 'MPN_MISMATCH_PO',
  IPN_MISMATCH_ASN = 'IPN_MISMATCH_ASN',
  IPN_MISMATCH_PO = 'IPN_MISMATCH_PO',
  CONTAINER_NOT_AT_LOCATION = 'CONTAINER_NOT_AT_LOCATION',
  CONTAINER_INACTIVE = 'CONTAINER_INACTIVE',
  OVERAGE_ASN = 'OVERAGE_ASN',
  OVERAGE_PO = 'OVERAGE_PO',
  MISMATCH_AGAINST_ASN = 'MISMATCH_AGAINST_ASN',
  SITE_SPECIFIC_DUPLICATE_SERIAL = 'SITE_SPECIFIC_DUPLICATE_SERIAL',
  COMPOSITE_DUPLICATE_SERIAL = 'COMPOSITE_DUPLICATE_SERIAL',
}

export enum ReceiveExecutionStatus {
  // Initial status
  STARTED_WORKFLOW = 'STARTED_WORKFLOW',

  // Workflow status
  COMPLETED_VALIDATION = 'COMPLETED_VALIDATION',
  COMPLETED_LINE_ITEM_RECONCILIATION = 'COMPLETED_LINE_ITEM_RECONCILIATION',
  COMPLETED_INVENTORY_UPDATE = 'COMPLETED_INVENTORY_UPDATE',
  COMPLETED_RECEIVE_STORE = 'COMPLETED_RECEIVE_STORE',
  COMPLETED_FINANCIAL_RECEIPT_CREATION = 'COMPLETED_FINANCIAL_RECEIPT_CREATION',
  COMPLETED_RECEIVE_NOTIFICATION_GENERATION = 'COMPLETED_RECEIVE_NOTIFICATION_GENERATION',

  // Failed Terminal status
  FAILED_VALIDATION = 'FAILED_VALIDATION',
  FAILED_LINE_ITEM_RECONCILIATION = 'FAILED_LINE_ITEM_RECONCILIATION',
  FAILED_INVENTORY_UPDATE = 'FAILED_INVENTORY_UPDATE',
  FAILED_RECEIVE_STORE = 'FAILED_RECEIVE_STORE',
  PARTIALLY_FAILED_INVENTORY_UPDATE = 'PARTIALLY_FAILED_INVENTORY_UPDATE',
  FAILED_FINANCIAL_RECEIPT_CREATION = 'FAILED_FINANCIAL_RECEIPT_CREATION',
  FAILED_RECEIPT_NOTIFICATION_GENERATION = 'FAILED_RECEIPT_NOTIFICATION_GENERATION',
  CONTAINER_NOT_IN_USER_CUSTODY = 'CONTAINER_NOT_IN_USER_CUSTODY',
  DUPLICATE_TOTE_EXISTS = 'DUPLICATE_TOTE_EXISTS',
  INVENTORY_ADJUSTMENT_SERIAL_FAILURE_DUPLICATE_INVENTORY_MODIFICATION = 'INVENTORY_ADJUSTMENT_SERIAL_FAILURE_DUPLICATE_INVENTORY_MODIFICATION',

  // Successful terminal status
  SUCCESSFULLY_COMPLETED_RECEIVE = 'SUCCESSFULLY_COMPLETED_RECEIVE',
}

export enum ReceiveExecutionType {
  PRECONDITION_RECEIVE = "PRECONDITION_RECEIVE",
  RMA = "RMA",
  INVENTORY_ADJUSTMENT = "INVENTORY_ADJUSTMENT"
}

export interface ReferenceQueryResponseItem {
  receiveReference: Receive;
  scannableIdFoundAs: ReceiveReferenceIdType;
  status: ValidationStatus;
  scanStartTime: number;
  siteName: string;
  clusterName: string;
  siteEquipmentId: string;
  shipmentMissingReasonCode: string;
}

export interface ModelResponseItem {
  referenceLineItems: [Model];
  paginationNextToken: string;
}

export type Receive = {
  referenceId: string;
  receiveReferenceId: string;
  externalReferenceValue: string;
  externalReferenceType: ReceiveType;
  type: ReceiveType;
  trackingId?: string;
  scannableId: string;
  expectedReceiveLocation?: string;
  expectedDeliveryDateTime: string;
  status?: ValidationStatus;
  buildId?: string;
  siteName?: string;
  clusterName?: string;
  models?: string[];
  supplier?: string;
  carrier?: string;
  reasonCode: string;
};

export enum AsyncState {
  CREATED = 'CREATED',
  FETCHING = 'FETCHING',
  IN_PROGRESS = 'IN_PROGRESS',
  OK = 'OK',
  ERROR = 'ERROR',
  WARNING = 'WARNING',
}

export type ContainerReceiveError = {
  id: string,
  errorCode: ContainerValidationError,
}

export type ReceiveErrors = ReceiveRequestValidationError | LineItemValidationError | ContainerReceiveError;

export interface Model {
  modelNumber: string;
  description: string;
  category?: string;
  containerIds?: string[];
  status?: AsyncState;
  referenceId?: string;
  referenceLineItemId?: string;
  referenceValue?: string;
  referenceLineItemNumber?: string;
  referenceType?: ReceiveType;
  externalReferenceType?: ReceiveType;
  orderNumber?: string;
  orderType?: string;
  orderLineItemNumber?: string;
  orderedMaterialDefinitionId?: string;
  orderedIPN?: string;
  orderedQuantity?: number;
  orderedMaterialCategory?: MaterialCategory;
  expectedMaterialDefinitionId?: string;
  expectedIPN?: string;
  expectedMPN?: string;
  supplier?: string;
  manufacturer?: string;
  buildId?: string;
  maximumExpectedQuantity?: number;
  currentReceivedQuantity?: number;
  warning?: boolean;
  totalSerials?: number;
  buildCategory?: string;
  tags: string[],
  rmaID?: string;
  rmaLineItemNumber: string;
  requisitionId: string;
  requisitionLineItemNumber: string;
  wwFaId: string;
  wwFaLineItemNumber: string;
  receiveReason: string;
  siteName: string;

  mappedOrderNumber: string;
  mappedOrderLineItemNumber: string;

  // UI generated uuid for MPN + IPN Combination
  mpnIpnId: string | undefined;

  // IPN mappping with ASN
  mappedIPN: null | string;

  // receiveExecutionUUId for tracking the state of model, as UI is submitting request per model
  receiveExecutionUUId?: string;

  receivedErrorsList?: ReceiveErrors[];

  metricsList?: MetricPair[];

  tagList?: any[];

  referenceLineItemStatus?: ValidationStatus;
};

export type ASNReferenceForPO = {
  receiveReference: Receive
}

export type GetASNReferencesResponse = {
  asnReferencesList: ASNReferenceForPO[]
  paginationNextToken: String
}

export enum SerialValidationCodes {
  SERIAL_NOT_EXIST = 'SERIAL_NOT_EXIST',
  SERIAL_DUPLICATE = 'SERIAL_DUPLICATE',
  SERIAL_DUPLICATE_COMPOSITE = 'SERIAL_DUPLICATE_COMPOSITE',
}

export enum SerialErrorType {
  ERROR = 'ERROR',
  WARNING = 'WARNING',
}

export interface SerialError {
  type: SerialErrorType;
  message: String;
  code: SerialValidationCodes;
}

export interface SerialRequest {
  referenceId: string;
  referenceLineItemId: string;
  mpn: string;
  ipn: string;
  serialId: string;
  inventoryAdjustmentWorkflow: boolean;
}
export interface Serial {
  containerId: string;
  status?: ValidationStatus;
  receiveStatus?: ReceiveStatus;
  receiveState?: AsyncState;
  errors: SerialError[];
  loading: boolean;
  called: boolean;
  serialNumber: string;
  id: string;
  modelId: string;
  manufacturer?: string;
  referenceLineItemId: string;
  state?: AsyncState;
  request?: SerialRequest;
  mpnIpnId?: string;
  siteSpecificSerialId?: string;
  externalReferenceValue?: string;
  referenceId?: string;
  metricsList?: MetricPair[];
}

export interface AsyncFetchState<TData = unknown> {
  data: TData;
  errors: string[];
  warnings?: string[];
  loading: boolean;
}

export type containersType = { [key: string]: string | undefined };

export interface ContainerInformation {
  type: ContainerType;
  cmsSiteLaunched: boolean;
}

export type ContainersSelection = { [key: string]: ContainerInformation | undefined };

export type UserPreferencesType<TData = unknown> = { [key: string]: TData };

export interface State {
  receiveReference?: AsyncFetchState<ReferenceQueryResponseItem[] | undefined>;
  receiveReferences: ReferenceQueryResponseItem[];
  models?: Model[];
  initialShipmentReferenceId?: string | null;
  initialPackagingId?: string | null;
  workRequestId?: string | null;
  initialWorkRequestlocationId?: string | null;
  shouldRefetchReference?: boolean;
  serviceType?: ServiceType;
  containers?: containersType | ContainersSelection;
  serials?: Serial[];
  scannedModel?: AsyncFetchState<ModelResponseItem[] | undefined>;
  erroredWithAttentionSerial?: null | Serial;
  activeReference: null | ReferenceQueryResponseItem;
  receiveReferenceList: ReceiveMaterialAsyncResponse[];
  userPreferences: UserPreferencesType;
  executions: ReceiveMaterialAsyncResponse[];
  hasPermissionsToAccessIA: boolean;
  hasPermissionsToAccessReceive: boolean;
}

export enum ReferenceLineItemValidationCode {
  'INACTIVE_RECEIVE_REFERENCE' = 'INACTIVE_RECEIVE_REFERENCE',
  'ASN_MPN_NOT_VALID' = 'ASN_MPN_NOT_VALID',
  'ASN_IPN_NOT_VALID' = 'ASN_IPN_NOT_VALID',
  'ASN_MPN_IPN_PAIRING_INVALID' = 'ASN_MPN_IPN_PAIRING_INVALID',
  'ASN_MPN_IPN_PAIRING_VALID_PO_INVALID' = 'ASN_MPN_IPN_PAIRING_VALID_PO_INVALID',
  'INACTIVE_RECEIVE_REFERENCE_ORDER' = 'INACTIVE_RECEIVE_REFERENCE_ORDER',
  'INACTIVE_REFERENCE_LINE_ITEM' = 'INACTIVE_REFERENCE_LINE_ITEM',
  'INACTIVE_REFERENCE_LINE_ITEM_ORDER' = 'INACTIVE_REFERENCE_LINE_ITEM_ORDER',
  'REQUIRE_MANUAL_PO_RECEIVE' = 'REQUIRE_MANUAL_PO_RECEIVE',
}

export type URIParams = {
  referenceId: string;
  receiveType: string;
  modelId: string;
};

export type Site = {
  label: string;
  value: string;
  locationId: string;
}

export interface ReceivedLineItemExecutionStatus {
  referenceLineItemId: string;
  lineItemExecutionStatus: ReceiveExecutionStatus;
  receivedManufacturerPartNumber: string;
  receivedInternalPartNumber: string;
  receivedMaterialDefinitionId: string;
  receivedManufacturer: String
  financiallyReceivedInternalPartNumber: string;
  financiallyReceivedMaterialDefinitionId: string;
  referenceLineItemStatus: ReferenceLineItemStatus;
  containerizedMaterialStatusList: ContainerizedMaterialStatus[];
  errorCode: LineItemValidationError;
}
export interface ReferenceLineItemStatus {
  status: string;
  statusCode: string;
  statusMessage: string;
}

export interface MPNToIPNMapping {
  mpn: string;
  associatedIpns: MPNToIPNPair[];
}

export interface MPNToIPNPair {
  ipn: string;
  description: string;
  manufacturer?: string;
  materialDefintionId: string;
  physicalSerialIdRequired: boolean;
}

export interface ContainerizedMaterialStatus {
  containerId: string;
  containerScannableId: string;
  receiveQuantity: number;
  receivedSerialList: SerialInfoStatus[];
  errorCode: ContainerValidationError;
  containerType: ContainerType;
}

export interface SerialInfoStatus {
  externalSerialId: string;
  siteSpecificSerialId: string;
  serialStatus: ReceiveExecutionStatus;
  errorCode: SerialValidationError;
  serialReceivedStatus: InventoryReceivedStatus;
}

export interface ContainerizedMaterial { }

export interface ReceivedLineItem {
  referenceLineItemId: string;
  manufacturerPartNumber: string;
  internalPartNumber: string;
  manufacturer: string;
  materialDefinitionId: string;
  containerizedMaterialList: ContainerizedMaterialStatus[];
  containerizedMaterialStatusList: ContainerizedMaterialStatus[];
  status: AsyncState;
  totalSerials: number;
}

export interface ReceiveMaterialAsyncRequest {
  clientToken: string;
  externalScannableId: string;
  receiveReferenceId: string;
  trackingId: String;
  receivedLineItemList: ReceivedLineItem[];
  receivedLineItem: ReceivedLineItem;
  receivedBy: string;
  receiveScanStartTime: number;
  receiveScanEndTime: number;
  siteName: String;
  clusterName: String;
  siteEquipmentId: String;
}

export interface IPNManufacturerPair {
  ipn: string,
  manufacturer: string,
}

export enum ReceiveRequestValidationError {
  'RECEIVE_REFERENCE_NOT_FOUND_VALIDATION' = 'RECEIVE_REFERENCE_NOT_FOUND_VALIDATION',
  'RECEIVE_REFERENCE_INACTIVE_VALIDATION' = 'RECEIVE_REFERENCE_INACTIVE_VALIDATION',
  'RECEIVE_REFERENCE_CLUSTER_NAME_NOT_FOUND_VALIDATION' = 'RECEIVE_REFERENCE_CLUSTER_NAME_NOT_FOUND_VALIDATION',
  'RECEIVE_REFENCE_UNEXPECTED_ERROR' = 'RECEIVE_REFENCE_UNEXPECTED_ERROR',
}

export enum LineItemValidationError {
  'LINE_ITEM_NOT_EXISTS_VALIDATION' = 'LINE_ITEM_NOT_EXISTS_VALIDATION',
  'LINE_ITEM_MAPPING_INCORRECT_VALIDATION' = 'LINE_ITEM_MAPPING_INCORRECT_VALIDATION',
  'LINE_ITEM_ERROR_STATUS_VALIDATION' = 'LINE_ITEM_ERROR_STATUS_VALIDATION',
  'LINE_ITEM_NOT_EXISTS_COUNT_RECONCILIATION' = 'LINE_ITEM_NOT_EXISTS_COUNT_RECONCILIATION',
  'LINE_ITEM_ERROR_STATUS_COUNT_RECONCILIATION' = 'LINE_ITEM_ERROR_STATUS_COUNT_RECONCILIATION',
  'UPDATE_COUNT_ASN_FAILURE_COUNT_RECONCILIATION' = 'UPDATE_COUNT_ASN_FAILURE_COUNT_RECONCILIATION',
  'UPDATE_COUNT_PO_FAILURE_COUNT_RECONCILIATION' = 'UPDATE_COUNT_PO_FAILURE_COUNT_RECONCILIATION',
  'LINE_ITEM_UNEXPECTED_ERROR' = 'LINE_ITEM_UNEXPECTED_ERROR',
}

export enum ContainerValidationError {
  'CONTAINER_INVALID_VALIDATION' = 'CONTAINER_INVALID_VALIDATION',
  'CONTAINER_UNEXPECTED_ERROR' = 'CONTAINER_UNEXPECTED_ERROR',
  'CONTAINER_NOT_IN_USER_CUSTODY' = 'CONTAINER_NOT_IN_USER_CUSTODY'
}

export enum SerialValidationError {
  'SERIAL_DUPLICATE_SITE_SPECIFIC_VALIDATION' = 'SERIAL_DUPLICATE_SITE_SPECIFIC_VALIDATION',
  'SERIAL_DUPLICATE_COMPOSITE_MATCH_VALIDATION' = 'SERIAL_DUPLICATE_COMPOSITE_MATCH_VALIDATION',
  'INVENTORY_ADJUSTMENT_SERIAL_FAILURE_DUPLICATE_INVENTORY_MODIFICATION' = 'INVENTORY_ADJUSTMENT_SERIAL_FAILURE_DUPLICATE_INVENTORY_MODIFICATION',
  'INVENTORY_ADJUSTMENT_SERIAL_FAILURE_UNEXPECTED_INVENTORY_MODIFICATION' = 'INVENTORY_ADJUSTMENT_SERIAL_FAILURE_UNEXPECTED_INVENTORY_MODIFICATION',
  'INVENTORY_ADJUSTMENT_SERIAL_FAILURE_THROTTLED_INVENTORY_MODIFICATION' = 'INVENTORY_ADJUSTMENT_SERIAL_FAILURE_THROTTLED_INVENTORY_MODIFICATION',
  'SERIAL_MPN_NOT_EXPECTED_IN_RECEIVE_VALIDATION' = 'SERIAL_MPN_NOT_EXPECTED_IN_RECEIVE_VALIDATION',
  'SCOS_RECEIPTS_NOTIFY_FAILURE_DEPENDENCY_NOTIFICATION' = 'SCOS_RECEIPTS_NOTIFY_FAILURE_DEPENDENCY_NOTIFICATION',
  'SERIAL_TOWNSEND_IDENTIFIER_COMBINATION_INVALID_VALIDATION' = 'SERIAL_TOWNSEND_IDENTIFIER_COMBINATION_INVALID_VALIDATION',
  'SERIAL_ALREADY_RECEIVED_FOR_SHIPMENT' = 'SERIAL_ALREADY_RECEIVED_FOR_SHIPMENT',
  'SERIAL_UNEXPECTED_ERROR' = 'SERIAL_UNEXPECTED_ERROR',
}

export enum InventoryReceivedStatus {
  'OK' = 'OK',
  'IN_PROGRESS' = 'IN_PROGRESS',
  'ERRORED' = 'ERRORED',
}

export enum ContainerType {
  BIN = 'BIN',
  TOTE = 'TOTE',
  PALLET = 'PALLET',
  USER = 'USER',
}

export interface GetContainerSuggestionsResponse {
  containers: ContainerResponse[]
}

export interface ContainerResponse {
  containerName: string
  quantity: number
}

export interface ReceiveMaterialAsyncResponse extends ReceiveMaterialAsyncRequest {
  receiveExecutionUUId: string;
  mpnIpnId: string;
  status?: AsyncState;
}

export interface GetReceiveStatusByExecutionIdResponse {
  receiveExecutionReadableId: string;
  receiveExecutionStatus: ReceiveExecutionStatus;
  externalScannableId: string;
  receiveReferenceId: string;
  trackingId: string;
  receivedBy: string;
  receiveSubmissionTime: string;
  errorCode: ReceiveRequestValidationError;
  inventoryReceiveStatus: InventoryReceivedStatus;
  receivedLineItemExecutionStatusList: [ReceivedLineItemExecutionStatus];
}

export type ActiveContainerType = { label: string; value: string, containerType?: 'BIN' | 'TOTE' };

export enum ServiceType { 'HTTP' = 'HTTP', 'GRAPHQL' = 'GRAPHQL' };

export interface ValidateSerialMetadataResponse {
  serialId: string;
  status: ValidationStatus,
  externalReferenceValue: string;
  id: string;
  mpn?: string;
  referenceLineItemId?: string;
}
